import { camelCase, startCase, toLower, upperFirst } from 'lodash';
import { flow, last, sortBy } from 'lodash/fp';
import { b2cPagesService, messageFormat } from '@clubmed/utils';
import * as trackingStorageApi from 'Tracking/tracking-storage-api';
import {
  BOOKING_STATUS_BOOKMARK,
  BOOKING_STATUS_EXPIRED_OPTION,
  BOOKING_STATUS_OPTION,
  BOOKING_STATUS_SEARCH,
} from 'Utils/booking-status/bookingStatus.constants';
import {
  BE,
  BE_TRACKING,
  PLE,
  PLE_OPTION,
  PLE_QUICK,
  PLE_QUICK_OPTION,
  SEARCH_LABEL_GA_MAP,
  STATIC_PAGE_TYPES,
  TRACK_LOGGED_STATUS,
  TRACK_SITE_SECTIONS,
} from './constants';
const { PAGE_TYPES } = b2cPagesService;
export const getReferenceType = (type: string | null | undefined) => {
  if ([BOOKING_STATUS_OPTION, BOOKING_STATUS_EXPIRED_OPTION].includes(type)) {
    return 'option';
  }

  if ([BOOKING_STATUS_BOOKMARK].includes(type)) {
    return 'tp_shopping';
  }

  if ([BOOKING_STATUS_SEARCH].includes(type)) {
    return 'tp_be';
  }

  return 'booking';
};
export const getPageSiteSection = ({ pageType }: { pageType: string }) => {
  switch (pageType) {
    case STATIC_PAGE_TYPES.BE:
      return TRACK_SITE_SECTIONS.BE;

    case STATIC_PAGE_TYPES.PLE:
    case STATIC_PAGE_TYPES.SHOPPING_NEWSLETTER:
    case STATIC_PAGE_TYPES.PLE_PAYMENT_CONFIRMATION:
      return TRACK_SITE_SECTIONS.CA;

    case STATIC_PAGE_TYPES.ERROR:
      return TRACK_SITE_SECTIONS.ERROR;

    case STATIC_PAGE_TYPES.PREBOOKING_FORM:
      return TRACK_SITE_SECTIONS.PREBOOKING_FORM;

    default:
      return TRACK_SITE_SECTIONS.SHOPPING;
  }
};
export const SCOPE_SITE_SECTION = {
  BE: 'booking_engine',
  CA: 'customer_account',
  ERROR: 'error',
  SHOPPING: 'shopping',
  PREBOOKING_FORM: 'prebooking_form',
};
export const getScopeSiteSection = ({ pageType }: { pageType: string }) => {
  switch (pageType) {
    case STATIC_PAGE_TYPES.BE:
      return SCOPE_SITE_SECTION.BE;

    case STATIC_PAGE_TYPES.PREBOOKING_FORM:
      return SCOPE_SITE_SECTION.PREBOOKING_FORM;

    case STATIC_PAGE_TYPES.PLE:
    case STATIC_PAGE_TYPES.SHOPPING_NEWSLETTER:
    case STATIC_PAGE_TYPES.PLE_PAYMENT_CONFIRMATION:
      return SCOPE_SITE_SECTION.CA;

    case STATIC_PAGE_TYPES.ERROR:
      return SCOPE_SITE_SECTION.ERROR;

    default:
      return SCOPE_SITE_SECTION.SHOPPING;
  }
};
export const getNeolaneId = ({
  isCxo,
  customerNeolaneId,
  neolaneId,
}: AppContext): string | null | undefined => {
  const cookie = (trackingStorageApi.get(BE_TRACKING) || {}) as { neolaneId?: string };
  return neolaneId || cookie?.neolaneId || (isCxo ? customerNeolaneId : null);
};
export const isPBFLandingPage = (asPath: string) => {
  return /^\/prebookingform(\/|$)/.test(asPath.split('?')[0]);
};
export const isQuotationLandingPage = (asPath: string) => {
  const shouldLoadShoppingGA4 = /^\/i\/.*$/.test(asPath);
  return shouldLoadShoppingGA4;
};
export const isQuotationSuccessPage = (asPath: string) => {
  const shouldLoadShoppingGA4 = /^\/quotation\/.*$/.test(asPath);
  return shouldLoadShoppingGA4;
};
export const getConnectionStatus = ({ isUserLogged, isUserQuickLogged }: AppContext) => {
  if (isUserQuickLogged) {
    return TRACK_LOGGED_STATUS.QUICK;
  }

  if (isUserLogged) {
    return TRACK_LOGGED_STATUS.FULL;
  }

  return TRACK_LOGGED_STATUS.NOT;
};
export const formatPayload = (payload: any, options: Record<string, string | null | undefined>) =>
  Object.keys(payload).reduce(
    (acc, key) => ({ ...acc, [key]: messageFormat(payload[key]).format(options) }),
    {},
  );
export function getPLECategoryGA(isUserQuickLogged: boolean) {
  return isUserQuickLogged ? PLE_QUICK : PLE;
}
export function getPLEPaymentConfirmationSiteSection({
  isOption,
  isUserQuickLogged,
}: {
  isOption?: boolean;
  isUserQuickLogged: boolean;
}) {
  if (isOption) {
    return isUserQuickLogged ? PLE_QUICK_OPTION : PLE_OPTION;
  }

  return getPLECategoryGA(isUserQuickLogged);
}
export function getSearchMappedTypeForLabelGA(type?: string) {
  return SEARCH_LABEL_GA_MAP[type || 'none'];
}
type SiteSectionParams = {
  isOption?: boolean;
  isUserQuickLogged?: boolean;
  pageType: string;
};
export function noCase(string: string) {
  return toLower(startCase(string));
}
export function pascalCase(string: string) {
  return upperFirst(camelCase(string));
}
export function getSiteSection({
  isOption = false,
  isUserQuickLogged = false,
  pageType,
}: SiteSectionParams) {
  switch (pageType) {
    case PAGE_TYPES.DESTINATION_MICE:
      return `M&E_${pascalCase(PAGE_TYPES.DESTINATION)}`;

    case PAGE_TYPES.HOME_MICE:
      return `M&E_${pascalCase(PAGE_TYPES.HOME)}`;

    case PAGE_TYPES.LANDING_MICE:
      return `M&E_${pascalCase(PAGE_TYPES.LANDING)}`;

    case PAGE_TYPES.RESORT_MICE:
      return `M&E_${pascalCase(PAGE_TYPES.RESORT)}`;

    case PAGE_TYPES.PREBOOKING:
      const siteSection = pageType.split('-')[0];
      return `Shopping_${pascalCase(siteSection)}`;

    case STATIC_PAGE_TYPES.BE:
      return BE;

    case STATIC_PAGE_TYPES.PLE:
    case STATIC_PAGE_TYPES.SHOPPING_NEWSLETTER:
      return getPLECategoryGA(isUserQuickLogged);

    case STATIC_PAGE_TYPES.PLE_PAYMENT_CONFIRMATION:
      return getPLEPaymentConfirmationSiteSection({
        isOption,
        isUserQuickLogged,
      });

    default:
      return `Shopping_${pascalCase(pageType)}`;
  }
}
export const TRACKING_ROOM_TYPE = {
  SUPERIOR: 'superior',
  DELUXE: 'deluxe',
  SUITE: 'suite',
  CHALET: 'chalet',
  VILLA: 'villa',
};
const TRACKING_ROOM_TYPE_ORDER = [
  TRACKING_ROOM_TYPE.SUPERIOR,
  TRACKING_ROOM_TYPE.DELUXE,
  TRACKING_ROOM_TYPE.SUITE,
  TRACKING_ROOM_TYPE.CHALET,
  TRACKING_ROOM_TYPE.VILLA,
];
export const TRACKING_ROOM_TYPE_BY_ACCOMMODATION_CATEGORY = {
  '000017': TRACKING_ROOM_TYPE.SUPERIOR,
  // club
  '000048': TRACKING_ROOM_TYPE.SUPERIOR,
  // club seaside view
  '000061': TRACKING_ROOM_TYPE.SUPERIOR,
  // superior
  '000022': TRACKING_ROOM_TYPE.SUPERIOR,
  // superior
  '000018': TRACKING_ROOM_TYPE.DELUXE,
  // deluxe
  '000039': TRACKING_ROOM_TYPE.DELUXE,
  // deluxe zen
  '000047': TRACKING_ROOM_TYPE.DELUXE,
  // deluxe seaside view
  '000056': TRACKING_ROOM_TYPE.DELUXE,
  // Deluxe Exclusive
  '000019': TRACKING_ROOM_TYPE.SUITE,
  // suite
  '000020': TRACKING_ROOM_TYPE.SUITE,
  // suite 5 tridents
  '000024': TRACKING_ROOM_TYPE.CHALET,
  // chalet
  '000028': TRACKING_ROOM_TYPE.VILLA, // villa
};
export function getTrackingRoomType(
  accommodations:
    | Array<Accommodation>
    | ReadonlyArray<Accommodation>
    | ReadonlyArray<{
        id: string;
        categoryId: string;
        comfortType: string;
        quantity: number;
      }>,
): string {
  const accommodation = flow(
    sortBy(({ categoryId }) =>
      TRACKING_ROOM_TYPE_ORDER.indexOf(TRACKING_ROOM_TYPE_BY_ACCOMMODATION_CATEGORY[categoryId]),
    ),
    last,
  )(accommodations);
  return (
    TRACKING_ROOM_TYPE_BY_ACCOMMODATION_CATEGORY[
      (accommodation as { categoryId?: string } | undefined)?.categoryId || ''
    ] || ''
  );
}
export const TRACKING_ACCOMMODATION_FILTER_TYPES = {
  accommodations: 'comfort_type',
  configuration: 'room_configuration',
  extra: 'other_criteria',
};
